<template>
    <div>
        <page-content-toolbar
            :projectName="`${project && project.name ? project.name : ' '}`"
            :title="$t('global.test')"
        />
        
          <v-card
            elevation="2"
            class="mx-auto"
            :loading="!ready"
          >
            <v-card-title>
              <v-switch
                  v-model="advancedModeEnabled"
                  :label="advancedModeEnabled && $t('advancedTest.test') || $t('standardTest.test')"
              ></v-switch>
            </v-card-title>
            <v-card-text v-if="!this.selectedStage">
              {{ $t('advancedTest.deploymentRequiredBeforeTesting') }}
            </v-card-text>
            <v-card-text v-if="this.selectedStage">
              <v-form 
                ref="form"
                v-model="valid"
              >
                <v-row class="mx-0">
                  <v-col cols="6">
                  <div class="d-flex flex-wrap mb-6 text-center">
                    <v-tooltip bottom 
                      v-for="(fact, index) in facts"
                      :key="index"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          class="ma-2 fact"
                          :color="`${fact.color} lighten-3`"
                          text-color="white"
                          label
                          small
                          @click="modelFacts.push(newFact(fact.value));"
                        >
                          {{ fact.value }}
                          
                        </v-btn>
                      </template>
                      <span>{{ $t('advancedTest.addFacts') }}</span>
                    </v-tooltip>
                  </div>
                
                  <v-combobox
                      required
                      v-model="modelFacts"
                      hide-no-data
                      :label="$t('advancedTest.facts')"
                      multiple
                      allow-overflow
                      :rules="[required]"
                      clearable
                      append-icon=""
                      append-outer-icon="mdi-content-copy"
                      @click:append-outer="copyTextFacts()"
                    >
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-text-field
                          v-if="editing === item"
                          v-model="editing.value"
                          autofocus
                          flat
                          :filled="false"
                          background-color="grey lighten-4"
                          dense
                          hide-details
                          solo
                          @keyup.enter="edit(index, item, facts)"
                          @click:append-outer="edit(index, item, facts)"
                          append-outer-icon="mdi-check"
                          @blur="editing = null; index = -1;"
                        >
                        </v-text-field>
                        <v-chip
                          v-else
                          :color="`${item.color} lighten-3`"
                          label
                          :input-value="selected"
                          v-bind="attrs"
                          @click.stop.prevent="edit(index, item, facts)"
                        >
                          <v-icon small left @click.stop.prevent="edit(index, item, facts)">mdi-square-edit-outline</v-icon>
                          {{ item.value }}
                          <v-icon
                            
                            right
                            @click.stop.prevent="parent.selectItem(item)"
                          >
                            mdi-close
                          </v-icon>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                
                  <v-col cols="6">
                    <v-combobox
                      v-model="modelOptions"
                      :items="options"
                      :label="$t('advancedTest.option')"
                      :filter="filterOptions"
                      clearable
                      multiple
                      v-show="advancedModeEnabled"
                    >
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                          <v-text-field
                            v-if="editing === item"
                            v-model="editing.value"
                            autofocus
                            flat
                            :filled="false"
                            background-color="grey lighten-4"
                            dense
                            hide-details
                            solo
                            @keyup.enter="editOption(index, item, options)"
                            @click:append-outer="editOption(index, item, options)"
                            append-outer-icon="mdi-check"
                            @blur="editing = null; index = -1;"
                          >
                          </v-text-field>
                          <v-chip
                            v-else
                            :color="`${item.color} lighten-3`"
                            label
                            :input-value="selected"
                            v-bind="attrs"
                            @click.stop.prevent="editOption(index, item, options)"
                          >
                            <v-icon small left @click.stop.prevent="editOption(index, item, options)">mdi-square-edit-outline</v-icon>
                            {{ item.value }}
                            <v-icon
                              
                              right
                              @click.stop.prevent="parent.selectItem(item)"
                            >
                              mdi-close
                            </v-icon>
                          </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-chip
                            :color="`${item.color} lighten-3`"
                            label
                          >
                            {{ item.value }}
                          </v-chip>
                        </template>
                    </v-combobox>
                  </v-col>
                </v-row>
                <v-row class="mx-0">
                  <v-col cols="6">
                    <v-slider
                      v-model="limit"
                      thumb-label="always"
                      :label="$t('advancedTest.numberOfSolutions')"
                      min="1"
                      max="20"
                    ></v-slider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-btn
                      color="primary"
                      tile
                      class="mx-1 px-7"
                      height="50px"
                      :disabled="!modelFacts.length || !modelOptions.length"
                      @click="proveList"
                      v-show="advancedModeEnabled"
                    >
                      {{ $t('advancedTest.prove') }}
                    </v-btn>
                    <v-btn
                      color="primary"
                      tile
                      class="mx-1 px-7"
                      height="50px"
                      :disabled="!modelFacts.length"
                      @click="proveAll"
                    >
                      {{ $t('advancedTest.solutions') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      :items="stages"
                      :label="$t('advancedTest.stage')"
                      dense
                      v-model="selectedStage"
                      return-object
                    >
                      <template v-slot:item="{ item }">
                        <span>{{ item.stage }} [{{ $t('sourcesAndDeployments.source') }}: {{ item.version }}]</span>
                      </template>
                      <template v-slot:selection="{ item }">
                        <span>{{ item.stage }} [{{ $t('sourcesAndDeployments.source') }}: {{ item.version }}]</span>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-container 
              v-if="!this.loadingSolution"
            > 
                <v-row>
                  <v-col
                    v-for="(solution, index) in solutions"
                    :key="index"
                    cols="12"
                    sm="4"
                  >
                    <v-card
                      class="pa-2"
                      outlined
                      :disabled="!solution.solutions.length"
                    >

                      <v-card-title>
                        <v-chip
                            :color="optionColor(solution.option)+` lighten-3`"
                            label
                          >
                            {{ solution.option }}
                          </v-chip>
                      </v-card-title>

                      <v-card-text>
                        <span v-if="!solution.solutions.length">
                          {{ $t('advancedTest.noResult') }}
                        </span>
                        <ol else>
                          <li
                            v-for="(sol) in solution.solutions"
                            :key="sol"
                          >
                            {{ sol }}
                          </li>
                        </ol>
                      </v-card-text>
                    </v-card>
              </v-col>
            </v-row>
          </v-container>

          </v-card>


          <v-card v-if="this.loadingSolution">
            <v-card-text>
              <v-progress-linear
                color="blue accent-4"
                indeterminate
                height="2"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
          
         
    </div>
</template>


<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import axios from 'axios';

import projectPageMixin from '@/mixins/project-page-mixin.js';

import deploymentService from '@/services/deployments.js';
import sourceService from '@/services/sources.js';
import testService from '@/services/tests.js';


export default {
  name: 'AdvancedTestProject',

  components: { PageContentToolbar },

  mixins: [ projectPageMixin ],

  data () { 
    return {
        advancedModeEnabled: false,
        loadingSolution: false,
        ready: false,
        solutions: [
          {
            option: '',
            response: '',
            solutions: []
          }
        ],
        valid: false,
        option: null,
        editing: null,
        limit: 5,
        activator: null,
        attach: null,
        colors: [
          'green', 'purple', 'indigo', 'cyan',
          'teal', 'orange', 'pink', 'amber', 
          'brown', 'blue-grey', 'deep-purple', 
          'deep-orange', 'lime', 'blue', 'light-green'
        ],
        index: -1,
        items: [],
        nonce: 0,
        menu: false,
        modelFacts: [],
        modelOptions: [],
        search: null,
        stages: [ ],
        selectedStage: null,
        projectId: null,
        deployment: null,
        facts: [],
        options: [],
        source: {
          id: '',
          source: '',
          version: '',
        },
      };
    },

    watch: {
      advancedModeEnabled(current, previous) {
        if(!current && current !== previous) {
          this.options = JSON.parse(JSON.stringify(this.initialOptions));
        }
      },

      modelOptions(val, prev) {
        if(!val || !prev || val.length === prev.length)  return
        
        this.modelOptions = val.flatMap(element => {
          if(typeof element === 'string') {
            // convert the typed string into a option object
            let newOptions = element.replace(/(\r\n|\n|\r)/gm,'').split(' ');
            newOptions = newOptions.filter(opt => opt !== '').map(opt => {
              let colorValue = this.optionColor(opt);
              opt = {
                value: opt,
                color: colorValue
              };
              return opt;
            });
            return newOptions;
          }
          
          return [ element ];
        });
      },

      modelFacts (val, prev) {
        if(!val || !prev || val.length === prev.length)  return

        this.modelFacts = val.flatMap(element => {
          if(typeof element === 'string') {
            // convert the typed string into a fact object
            let newFacts = element.replace(/(\r\n|\n|\r)/gm,'').split(' ');
            newFacts = newFacts.filter(fact => fact !== '').map(fact => {
              let predicate = this.predicateToFact(fact);
              let colorValue = this.predicateColor(predicate);
              fact = {
                value: predicate,
                color: colorValue,
                predicate: true,
              };
              return fact;
            });
            return newFacts;
          }
          else if (element.predicate) {
            // fact suggested
            let predicate = this.predicateToFact(element.value);
            let colorValue = this.predicateColor(predicate);
            return {
              value: predicate,
              color: colorValue,
              predicate: true,
            };
          }

          return [ element ];
        });
      },

      selectedStage(value) {
        this.solutions = []
        this.modelFacts = []
        this.modelOptions = []
        Promise.all([
          sourceService.getSource(this.projectId, value.sourceId),
          this.getDeploymentDetails(value.deploymentId)    
        ])
        .then((responses) => {
          this.source = responses[0].data;
          const decisionPolicy = sourceService.parseGorgias(this.decodedSource)
          const errors = []
          if(!decisionPolicy.facts || decisionPolicy.facts.size === 0) {
            errors.push('No facts found. Missing dynamic declaration?')
          }
          else {
            this.facts = [...decisionPolicy.facts].map(f => {
              f = {
                value: f.name + "/" + f.arite,
                color: this.colors[this.nonce++ % this.colors.length] 
              }
              return f
            });
          }

          if(!decisionPolicy.options || decisionPolicy.options.size == 0) {
            errors.push('No option found')
          }

          this.options = [...decisionPolicy.options].map(opt => {
            opt = {
              value: opt.signature,
              color: this.colors[this.nonce++ % this.colors.length] 
            }
            return opt
          });
          this.initialOptions = JSON.parse(JSON.stringify(this.options));

          this.deployment = responses[1].data;
          if(errors.length > 0)
            this.$emit('raison-event', { type: 'error', messages: errors });
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        })
        .finally(() => {
          this.ready = true;
        });
      },
    },

    computed: {
      decodedSource() {
        return window.atob(this.source.source);
      },
      firstOptions() {
        return this.options.slice(0,20);
      }
    },

    methods: {
      copyTextFacts() {
        if(navigator.clipboard) {
          navigator.clipboard.writeText(this.modelFacts.map(f => f.value).join(', ')).then(() => {
            this.$emit('raison-event', { type: 'success', messages: [this.$t('global.textCopied')] });
          }).catch(() => {
            this.$emit('raison-event', { type: 'error', messages: [this.$t('global.copyFailed')] });
          });
        }
        else {
            this.$emit('raison-event', { type: 'error', messages: [this.$t('global.copyFailed')] });
        }
      },

      required(value) {
        if (value instanceof Array && value.length == 0) {
          return 'Required.';
        }
        return !!value || 'Required.';
      },

      newFact(predicate) {
        let fact = this.predicateToFact(predicate);
        let colorValue = this.predicateColor(fact);
        return {
          // text: fact,
          value: fact,
          color: colorValue,
        };
      },

      predicateToFact(predicate) {
        if(!predicate.includes('/')) return predicate;
        let elements = predicate.split('/');
        let nbParams = parseInt(elements[1],10);
        let fact = elements[0];
        if(nbParams > 0) fact = fact + '(';
        let delta = 'X'.charCodeAt();
        for(let seq=0; seq<nbParams; seq++) {
          if(seq > 3) {
            delta = 'A'.charCodeAt();
          }
          fact = fact + String.fromCharCode(delta + seq);
          if(seq + 1 < nbParams) {
            fact = fact + ',';
          }
          else if(seq + 1 === nbParams) {
            fact = fact + ')';
          }
        }
        return fact;
      },

      editOption (index, item) {
        item.color = this.optionColor(item.value)
        if (!this.editing) {
          this.editing = item
          this.index = index
        } else {
          this.editing = null
          this.index = -1
        }
      },

      edit (index, item) {
        item.color = this.predicateColor(item.value)
        if (!this.editing) {
          this.editing = item
          this.index = index
        } else {
          this.editing = null
          this.index = -1
        }
      },

      filterOptions (item, queryText) {
        const hasValue = val => val != null ? val : ''

        const text = hasValue(item.value)
        const query = hasValue(queryText)

        return text.toString()
          .toLowerCase()
          .indexOf(query.toString().toLowerCase()) > -1
      },

      validate () {
        this.$refs.form.validate();
      },

      prove(option) {
        let config = { headers: { 'Content-Type': 'application/json' }};
        config.headers[this.deployment.apiKeyName] = this.deployment.apiKeySecret;
        return axios.post(this.deployment.address, 
          {
            facts: this.modelFacts.map(v => v.value),
            prove: 'prove(['+option+'], Explanation)',
            limit: this.limit
          }, 
          config)
        .then(proveResponse => {
          if(!this.solutions) this.solutions = []
          this.solutions.push({ 
              option: option, 
              response: proveResponse.data,
              solutions: testService.parseSolutions(proveResponse.data),
            });
          })
        .catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] });
          })
        .finally(() => this.solutions= this.solutions.sort((a, b) => { 
          if( a.response.has_solutions && !b.response.has_solutions ) {
            return -1
          }
          else if( !a.response.has_solutions && b.response.has_solutions ) {
            return 1
          }
          return a.option.localeCompare(b.option)
        }))
      },

      proveList() {
        this.loadingSolution = true;
        this.solutions = []
        
        const allPromises = [];
        this.modelOptions.forEach((option) => {
          allPromises.push(this.prove(option.value));
        });

        Promise.all(
          allPromises
        )
        .finally(() => {
          this.loadingSolution = false;
        })
      },

      proveAll() {
        this.loadingSolution = true;
        this.solutions = []
        // console.log('all:'+this.options)

        if(this.firstOptions.length < this.options.length) {
          this.$emit('raison-event', { type: 'error', messages: [this.$t('advancedTest.tooManyOptions', {limit: this.firstOptions.length})] });
        }

        const allPromises = [];
        this.firstOptions.forEach((option) => {
          allPromises.push(this.prove(option.value));
        });

        Promise.all(
          allPromises
        )
        .finally(() => {
          this.loadingSolution = false;
        })

      },

      getAvailableStages() {
        return deploymentService.getAvailableStages(this.projectId).then(res => {
          if(res.data && res.data.length > 0) {
            this.stages.push(...res.data);
            this.selectedStage = res.data[0];
          }
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        })
        .finally(() => {
          this.ready = true
        });
      },

      getDeploymentDetails(deploymentId) {
        return deploymentService.getDeployment(this.projectId, deploymentId);
      },

      optionColor(option) {
        let end = option.indexOf('(');
        if(end < 0) end = option.length;
        const optionPrefix = option.substring(0,end);
        const ref = this.options.find(opt => {
          let end = opt.value.indexOf('(')
          if(end < 0) end = opt.value.length
          const prefix = opt.value.substring(0, end);
          return optionPrefix === prefix
        })
        if(ref) {
          return ref.color;
        }
        return 'red accent-4'
      },

      predicateColor(predicate) {
        const ref = this.facts.find(it => {
          const prefix = it.value.split('/')[0];
          return predicate.replace(/\(.*\)/gm, '') === prefix
        });
        let choosenColor;
        if(ref) {
          choosenColor = ref.color;
        }
        else if(predicate.startsWith('rule')) {
          choosenColor = 'amber darken-1';
        }
        else {
          choosenColor = 'red accent-4';
        }
        return choosenColor;
      },

},

  created () {
    this.solutions = null;
    this.projectId = this.$route.params.id;
    this.items.push({ header: this.$t('advancedTest.selectAFactOrCreateOne') });
    this.getAvailableStages();
  },
};
</script>

<style scoped>
.fact.v-btn {
  text-transform: none;
}
</style>
